<template>
    <base-dialog :value="value" :title="editMode ? $t('exercises.edit_set') : $t('exercises.add_set')" :width="500"
        :fullscreen="isMobile" @input="onInput" @close="$emit('close')">
        <div class="sf-fields">
            <!-- REPS/TIME -->
            <div class="sf-row">
                <kimsa-text-field class="sf-flex" :label="repsLabel" dense no-elevation force-value custom-keypress
                    :input-mode="repsType && repsType.value === 'reps' ? 'text' : 'numeric'"
                    :prepend-inner-icon="repsType && icons[repsType.value] || icons.reps" :value="reps"
                    @keypress="onRepsKeypress" @change="$emit('update:reps', $event)" />
                <kimsa-combobox v-if="isFirstSet" class="sf-type" :label="$t('exercises.type')" dense
                    :placeholder="$t('common.select_placeholder')" :items="repsOptions"
                    @change="$emit('update:repsType', $event)" :value="repsType" ref="repsType" />
            </div>

            <!-- RPE/PERCENTAGE/RIR -->
            <div class="sf-row" v-if="rpeType">
                <kimsa-text-field class="sf-flex" :label="rpeLabel" dense no-elevation force-value custom-keypress
                    :input-mode="hasDecimals(rpeType) ? 'decimal' : 'numeric'"
                    :prepend-inner-icon="rpeType && icons[rpeType.value] || icons.rpe"
                    :value="rpe" @keypress="onRpeKeypress" @blur="maxValue('rpe', maxValueRpeType())"
                    @change="onRpeChange" />
                <kimsa-combobox v-if="isFirstSet" class="sf-type" :label="$t('exercises.type')" dense
                    :placeholder="$t('common.select_placeholder')" :items="rpeOptions"
                    @change="$emit('update:rpeType', $event)" :value="rpeType" ref="rpeType" />
            </div>

            <!-- TEMPO/ADDITIONAL -->
            <div class="sf-row" v-if="tempoType">
                <kimsa-text-field class="sf-flex" :label="tempoLabel" dense no-elevation force-value custom-keypress
                    input-mode="numeric" :max-length="tempoType && tempoType.value === 'tempo' ? undefined : 3"
                    :prepend-inner-icon="tempoType && icons[tempoType.value] || icons.tempo" :value="tempo"
                    @keypress="onTempoKeypress" @change="$emit('update:tempo', $event)" />
                <kimsa-combobox v-if="isFirstSet" class="sf-type" :label="$t('exercises.type')" dense
                    :placeholder="$t('common.select_placeholder')" :items="tempoOptions"
                    @change="$emit('update:tempoType', $event)" :value="tempoType" ref="tempoType" />
            </div>

            <!-- WEIGHT -->
            <kimsa-text-field v-if="hasWeight" :label="$t('exercises.weight')" dense no-elevation force-value
                custom-keypress input-mode="numeric" :prepend-inner-icon="icons.weight" :value="weight"
                @keypress="onNumberKeypress" @change="$emit('update:weight', $event)" />

            <!-- INTENSITY -->
            <div class="sf-row" v-if="intensityType">
                <kimsa-text-field class="sf-flex" :label="intensityLabel" dense no-elevation force-value custom-keypress
                    :input-mode="hasDecimals(intensityType) ? 'decimal' : 'text'"
                    :prepend-inner-icon="intensityType && icons[intensityType.value] || icons.beat_min"
                    :value="intensity" @keypress="onIntensityKeypress" @change="onIntensityChange" />
                <kimsa-combobox v-if="isFirstSet" class="sf-type" :label="$t('exercises.type')" dense
                    :placeholder="$t('common.select_placeholder')" :items="intensityOptions"
                    @change="$emit('update:intensityType', $event)" :value="intensityType" ref="intensityType" />
            </div>

            <!-- MICROPAUSE -->
            <kimsa-text-field v-if="hasMicroPause" :label="$t('exercises.micropause')" dense no-elevation force-value
                custom-keypress input-mode="numeric" :prepend-inner-icon="icons.micropause" :value="micropause"
                @keypress="onNumberKeypress" @change="$emit('update:micropause', $event)" />
        </div>

        <template #footer>
            <dialog-actions hide-cancel :confirm-label="$t('common.save')" :disabled="disabledSave"
                @confirm="$emit('save')" />
        </template>
    </base-dialog>
</template>

<script>
import BaseDialog from "@/components/common/BaseDialog";
import {
    SET_TYPE_ICONS, validateRepsKey, validateRpeKey, validateTempoKey, validateIntensityKey, validateNumberKey,
    cleanRpeValue, cleanIntensityValue, isDecimalSetType
} from "@/utils/Exercises";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import DialogActions from "@/components/common/DialogActions";

export default {
    name: "SetFormDialog",
    components: { BaseDialog, KimsaCombobox, KimsaTextField, DialogActions },
    props: {
        value: { type: Boolean, default: false },
        editMode: { type: Boolean, default: false },
        isFirstSet: { type: Boolean, default: false },
        hasWeight: { type: Boolean, default: false },
        hasMicroPause: { type: Boolean, default: false },
        disabledSave: { type: Boolean, default: false },
        repsOptions: { type: Array, default: () => [] },
        rpeOptions: { type: Array, default: () => [] },
        tempoOptions: { type: Array, default: () => [] },
        intensityOptions: { type: Array, default: () => [] },
        reps: { type: [String, Number], default: 1 },
        rpe: { type: [String, Number], default: 0 },
        tempo: { type: [String, Number], default: 0 },
        weight: { type: [String, Number], default: 0 },
        intensity: { type: [String, Number], default: 0 },
        micropause: { type: [String, Number], default: 0 },
        repsType: { type: Object, default: null },
        rpeType: { type: Object, default: null },
        tempoType: { type: Object, default: null },
        intensityType: { type: Object, default: null },
    },
    computed: {
        icons() {
            return SET_TYPE_ICONS
        },
        repsLabel(vm = this) {
            return vm.repsType && vm.repsType.name || vm.$t('exercises.reps')
        },
        rpeLabel(vm = this) {
            return vm.rpeType && vm.rpeType.name || vm.$t('exercises.rpe')
        },
        tempoLabel(vm = this) {
            return vm.tempoType && vm.tempoType.name || vm.$t('exercises.tempo')
        },
        intensityLabel(vm = this) {
            return vm.intensityType && vm.intensityType.name || vm.$t('exercises.intensity')
        },
    },
    methods: {
        onInput(val) {
            if (!val) this.$emit('close')
        },
        hasDecimals(type) {
            return isDecimalSetType(type)
        },
        onRepsKeypress(event) {
            validateRepsKey(event)
        },
        onNumberKeypress(event) {
            validateNumberKey(event)
        },
        onRpeKeypress(event) {
            validateRpeKey(event, this.rpeType)
        },
        onTempoKeypress(event) {
            validateTempoKey(event, this.tempoType)
        },
        onIntensityKeypress(event) {
            validateIntensityKey(event, this.intensityType)
        },
        onRpeChange(value) {
            this.$emit('update:rpe', cleanRpeValue(value, this.rpeType))
        },
        onIntensityChange(value) {
            this.$emit('update:intensity', cleanIntensityValue(value, this.intensityType))
        },
        maxValue(attribute, max) {
            const vm = this
            if (max === undefined) return
            if (Number(vm[attribute]) > max) vm.$emit(`update:${attribute}`, max)
        },
        maxValueRpeType() {
            const vm = this
            if (vm.rpeType) {
                if (vm.rpeType.value === 'percentage') return 100
                if (vm.rpeType.value === 'rir' || vm.rpeType.value === 'rpe') return 10
            }
            return undefined
        },
    },
}
</script>

<style scoped>
.sf-fields {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.sf-row {
    display: flex;
    align-items: flex-end;
    gap: 8px;
}

.sf-flex {
    flex: 1 1 0;
    min-width: 0;
}

.sf-type {
    flex: 0 0 150px;
    min-width: 0;
}

@media (max-width: 480px) {
    .sf-type {
        flex: 0 0 120px;
    }
}
</style>
